import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getAllExporters } from 'common/src/api/exporters';
import { getAllSpecies } from 'common/src/api/species';
import type { Exporter, Species } from 'common/src/utils/types';
import { SEASONS } from 'common/src/utils/definitions';
import {
  addExternalApiGrant,
  createExternalApiClient,
  deleteExternalApiGrant,
  externalMarketApiBaseUrl,
  issueExternalApiKey,
  listExternalApiClients,
  revokeExternalApiKey,
  updateExternalApiClient,
  type ExternalApiClient,
  type ExternalApiGrant,
  type ExternalApiScope,
  type IssuedExternalApiKey,
} from '../../../services/externalMarketApiAdmin';
import {
  Alert,
  EmptyState,
  LoadingState,
  PageHeader,
  PageLayout,
  useConfirm,
} from '../../../components/ui';
import customerGuideUrl from '../../../../../../docs/external-market-data-api-customer-guide.md?url';
import openApiUrl from '../../../../../../docs/external-market-data-api.openapi.yaml?url';

const ALL_SCOPES: Array<{
  value: ExternalApiScope;
  label: string;
  description: string;
}> = [
  {
    value: 'market:read',
    label: 'Leer Market Data',
    description: 'Catálogo, observaciones, resúmenes y Daily Market.',
  },
  {
    value: 'follows:read',
    label: 'Listar seguidos',
    description: 'Consultar exportadoras seguidas por el cliente API.',
  },
  {
    value: 'follows:write',
    label: 'Administrar seguidos',
    description: 'Agregar y eliminar exportadoras seguidas.',
  },
];

const configuredSeasons = Object.values(SEASONS).map((season) => season.name);
const today = new Date();
const currentSeasonStartYear =
  today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1;
const currentSeason = `${currentSeasonStartYear}-${currentSeasonStartYear + 1}`;
const seasonOptions = Array.from(
  new Set([...configuredSeasons, currentSeason])
).sort((a, b) => b.localeCompare(a));
const defaultSeason = configuredSeasons.at(-1) ?? currentSeason;

interface GrantForm {
  speciesId: string;
  season: string;
  dataFrom: string;
  dataTo: string;
}

interface KeyForm {
  name: string;
  expiresOn: string;
  scopes: ExternalApiScope[];
}

interface IssuedCredentialContext {
  key: IssuedExternalApiKey;
  clientName: string;
  exporterName: string;
  grant: GrantForm | null;
}

const defaultGrantForm = (): GrantForm => ({
  speciesId: '2',
  season: defaultSeason,
  dataFrom: '',
  dataTo: '',
});

const defaultKeyForm = (clientName = ''): KeyForm => ({
  name: clientName ? `${clientName} principal` : 'Credencial principal',
  expiresOn: '',
  scopes: ALL_SCOPES.map((scope) => scope.value),
});

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'Ocurrió un error inesperado.';
}

function expiresAt(expiresOn: string): string | null {
  return expiresOn
    ? new Date(`${expiresOn}T23:59:59.999Z`).toISOString()
    : null;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Nunca';
  return new Intl.DateTimeFormat('es-CL', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function statusColor(status: ExternalApiClient['status']) {
  if (status === 'active') return 'success' as const;
  if (status === 'suspended') return 'warning' as const;
  return 'danger' as const;
}

function scopeLabel(scope: ExternalApiScope): string {
  return ALL_SCOPES.find((item) => item.value === scope)?.label ?? scope;
}

function seasonDates(season: string): { dateFrom: string; dateTo: string } {
  const match = season.match(/^(\d{4})-(\d{4})$/);
  if (!match) return { dateFrom: '', dateTo: '' };
  return {
    dateFrom: `${match[1]}-07-01`,
    dateTo: `${match[2]}-06-30`,
  };
}

function downloadJson(filename: string, value: unknown) {
  const blob = new Blob([JSON.stringify(value, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

const SuperadminApiAccess = () => {
  const [clients, setClients] = useState<ExternalApiClient[]>([]);
  const confirm = useConfirm();
  const [exporters, setExporters] = useState<Exporter[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [provisioningStep, setProvisioningStep] = useState('');

  const [exporterId, setExporterId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientNameTouched, setClientNameTouched] = useState(false);
  const [minuteLimit, setMinuteLimit] = useState('60');
  const [dailyLimit, setDailyLimit] = useState('5000');
  const [grantForm, setGrantForm] = useState<GrantForm>(defaultGrantForm);
  const [keyForm, setKeyForm] = useState<KeyForm>(() => defaultKeyForm());

  const [grantClient, setGrantClient] = useState<ExternalApiClient | null>(
    null
  );
  const [grantModalForm, setGrantModalForm] =
    useState<GrantForm>(defaultGrantForm);
  const [savingGrant, setSavingGrant] = useState(false);

  const [keyClient, setKeyClient] = useState<ExternalApiClient | null>(null);
  const [keyModalForm, setKeyModalForm] = useState<KeyForm>(() =>
    defaultKeyForm()
  );
  const [issuingKey, setIssuingKey] = useState(false);

  const [issuedCredential, setIssuedCredential] =
    useState<IssuedCredentialContext | null>(null);
  const [credentialSaved, setCredentialSaved] = useState(false);
  const [updatingClientId, setUpdatingClientId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const exporterById = useMemo(
    () => new Map(exporters.map((exporter) => [exporter.id, exporter])),
    [exporters]
  );
  const speciesById = useMemo(
    () => new Map(species.map((item) => [item.id, item])),
    [species]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [clientRows, exporterRows, speciesRows] = await Promise.all([
        listExternalApiClients(),
        getAllExporters(),
        getAllSpecies(),
      ]);
      setClients(clientRows);
      setExporters(exporterRows);
      setSpecies(speciesRows);
      if (speciesRows.length > 0) {
        setGrantForm((current) => {
          if (
            speciesRows.some((item) => String(item.id) === current.speciesId)
          ) {
            return current;
          }
          const fallback =
            speciesRows.find((item) => item.id === 2) ?? speciesRows[0];
          return { ...current, speciesId: String(fallback.id) };
        });
      }
    } catch (error) {
      toast.error(`No se pudo cargar el acceso API: ${errorMessage(error)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const validateGrant = (form: GrantForm): string | null => {
    if (!form.speciesId) return 'Selecciona una especie.';
    if (!form.season) return 'Selecciona una temporada.';
    if (form.dataFrom && form.dataTo && form.dataFrom > form.dataTo) {
      return 'La fecha desde no puede ser posterior a la fecha hasta.';
    }
    return null;
  };

  const validateKey = (form: KeyForm): string | null => {
    if (!form.name.trim()) return 'Ingresa un nombre para la credencial.';
    if (form.scopes.length === 0) return 'Selecciona al menos un permiso.';
    if (
      form.expiresOn &&
      new Date(`${form.expiresOn}T23:59:59.999Z`).getTime() <= Date.now()
    ) {
      return 'El vencimiento debe estar en el futuro.';
    }
    return null;
  };

  const handleExporterChange = (value: string) => {
    setExporterId(value);
    if (!clientNameTouched) {
      const exporter = exporterById.get(Number(value));
      setClientName(exporter ? `${exporter.name} Market Data API` : '');
      setKeyForm((current) => ({
        ...current,
        name: exporter ? `${exporter.name} principal` : 'Credencial principal',
      }));
    }
  };

  const toggleScope = (
    scope: ExternalApiScope,
    checked: boolean,
    setForm: React.Dispatch<React.SetStateAction<KeyForm>>
  ) => {
    setForm((current) => ({
      ...current,
      scopes: checked
        ? Array.from(new Set([...current.scopes, scope]))
        : current.scopes.filter((item) => item !== scope),
    }));
  };

  const resetProvisionForm = () => {
    setExporterId('');
    setClientName('');
    setClientNameTouched(false);
    setMinuteLimit('60');
    setDailyLimit('5000');
    setGrantForm(defaultGrantForm());
    setKeyForm(defaultKeyForm());
  };

  const handleProvision = async () => {
    if (!exporterId) {
      toast.error('Selecciona la exportadora cliente.');
      return;
    }
    if (!clientName.trim()) {
      toast.error('Ingresa el nombre del cliente API.');
      return;
    }
    const parsedMinuteLimit = Number(minuteLimit);
    const parsedDailyLimit = Number(dailyLimit);
    if (
      !Number.isInteger(parsedMinuteLimit) ||
      parsedMinuteLimit < 1 ||
      !Number.isInteger(parsedDailyLimit) ||
      parsedDailyLimit < 1
    ) {
      toast.error('Los límites deben ser números enteros positivos.');
      return;
    }
    const grantError = validateGrant(grantForm);
    if (grantError) {
      toast.error(grantError);
      return;
    }
    const keyError = validateKey(keyForm);
    if (keyError) {
      toast.error(keyError);
      return;
    }

    setProvisioning(true);
    let createdClient: ExternalApiClient | null = null;
    try {
      setProvisioningStep('Creando cliente API…');
      createdClient = await createExternalApiClient({
        name: clientName.trim(),
        exporter_id: Number(exporterId),
        minute_limit: parsedMinuteLimit,
        daily_limit: parsedDailyLimit,
      });

      setProvisioningStep('Otorgando acceso a los datos…');
      await addExternalApiGrant(createdClient.id, {
        species_id: Number(grantForm.speciesId),
        season: grantForm.season,
        data_from: grantForm.dataFrom || null,
        data_to: grantForm.dataTo || null,
      });

      setProvisioningStep('Generando token de un solo uso…');
      const key = await issueExternalApiKey(createdClient.id, {
        name: keyForm.name.trim(),
        scopes: keyForm.scopes,
        expires_at: expiresAt(keyForm.expiresOn),
      });

      const exporter = exporterById.get(Number(exporterId));
      setIssuedCredential({
        key,
        clientName: createdClient.name,
        exporterName: exporter?.name ?? `Exportadora ${exporterId}`,
        grant: grantForm,
      });
      setCredentialSaved(false);
      resetProvisionForm();
      toast.success('Acceso creado. Guarda el token antes de cerrar.');
    } catch (error) {
      const prefix = createdClient
        ? `El cliente "${createdClient.name}" quedó creado. Puedes completar el acceso desde su ficha. `
        : '';
      toast.error(`${prefix}${errorMessage(error)}`);
    } finally {
      setProvisioning(false);
      setProvisioningStep('');
      await loadData();
    }
  };

  const openGrantModal = (client: ExternalApiClient) => {
    setGrantClient(client);
    setGrantModalForm(defaultGrantForm());
  };

  const handleAddGrant = async () => {
    if (!grantClient) return;
    const validationError = validateGrant(grantModalForm);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSavingGrant(true);
    try {
      await addExternalApiGrant(grantClient.id, {
        species_id: Number(grantModalForm.speciesId),
        season: grantModalForm.season,
        data_from: grantModalForm.dataFrom || null,
        data_to: grantModalForm.dataTo || null,
      });
      toast.success('Acceso de datos guardado.');
      setGrantClient(null);
      await loadData();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSavingGrant(false);
    }
  };

  const openKeyModal = (client: ExternalApiClient) => {
    setKeyClient(client);
    setKeyModalForm(defaultKeyForm(client.name));
  };

  const handleIssueKey = async () => {
    if (!keyClient) return;
    const validationError = validateKey(keyModalForm);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setIssuingKey(true);
    try {
      const key = await issueExternalApiKey(keyClient.id, {
        name: keyModalForm.name.trim(),
        scopes: keyModalForm.scopes,
        expires_at: expiresAt(keyModalForm.expiresOn),
      });
      setIssuedCredential({
        key,
        clientName: keyClient.name,
        exporterName:
          exporterById.get(keyClient.exporter_id ?? -1)?.name ?? 'Sin vincular',
        grant: null,
      });
      setCredentialSaved(false);
      setKeyClient(null);
      toast.success('Token generado. Guárdalo antes de cerrar.');
      await loadData();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setIssuingKey(false);
    }
  };

  const handleClientStatus = async (client: ExternalApiClient) => {
    const nextStatus = client.status === 'active' ? 'suspended' : 'active';
    setUpdatingClientId(client.id);
    try {
      await updateExternalApiClient(client.id, { status: nextStatus });
      toast.success(
        nextStatus === 'active' ? 'Cliente reactivado.' : 'Cliente suspendido.'
      );
      await loadData();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setUpdatingClientId(null);
    }
  };

  const handleDeleteGrant = async (grant: ExternalApiGrant) => {
    if (
      !(await confirm({
        title: '¿Eliminar este acceso de datos?',
        isDestructive: true,
      }))
    )
      return;
    setRemovingId(grant.id);
    try {
      await deleteExternalApiGrant(grant.id);
      toast.success('Acceso eliminado.');
      await loadData();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setRemovingId(null);
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    if (
      !(await confirm({
        title: '¿Revocar esta credencial?',
        message: 'La acción es inmediata.',
        confirmLabel: 'Revocar',
        isDestructive: true,
      }))
    ) {
      return;
    }
    setRemovingId(keyId);
    try {
      await revokeExternalApiKey(keyId);
      toast.success('Credencial revocada.');
      await loadData();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setRemovingId(null);
    }
  };

  const copyText = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch {
      toast.error('No se pudo copiar al portapapeles.');
    }
  };

  const handoffText = useMemo(() => {
    if (!issuedCredential) return '';
    return [
      'Cherry Market External Market Data API',
      `Customer: ${issuedCredential.clientName}`,
      `Linked exporter: ${issuedCredential.exporterName}`,
      `Base URL: ${externalMarketApiBaseUrl}`,
      `Token: ${issuedCredential.key.token}`,
      `Scopes: ${issuedCredential.key.scopes.join(', ')}`,
      `Expires: ${issuedCredential.key.expires_at ?? 'No expiration'}`,
      '',
      'Authentication header:',
      `Authorization: Bearer ${issuedCredential.key.token}`,
      '',
      'First request:',
      `GET ${externalMarketApiBaseUrl}/status`,
      '',
      'Treat the token as a password. Use it only from your backend or DWH; do not embed it in browser code.',
    ].join('\n');
  }, [issuedCredential]);

  const downloadPostmanCollection = () => {
    if (!issuedCredential) return;
    const grant = issuedCredential.grant;
    const inferredDates = seasonDates(grant?.season ?? defaultSeason);
    const variables = {
      speciesId: grant?.speciesId ?? '2',
      season: grant?.season ?? defaultSeason,
      dateFrom: grant?.dataFrom || inferredDates.dateFrom,
      dateTo: grant?.dataTo || inferredDates.dateTo,
    };
    const authHeader = {
      key: 'Authorization',
      value: 'Bearer {{token}}',
      type: 'text',
    };
    downloadJson('cherry-market-api.postman_collection.json', {
      info: {
        name: `Cherry Market - ${issuedCredential.clientName}`,
        description:
          'Server-to-server Market Data API. Keep the token secret. Start with Status and List exporters.',
        schema:
          'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
      },
      variable: [
        { key: 'base_url', value: externalMarketApiBaseUrl },
        { key: 'token', value: issuedCredential.key.token, type: 'secret' },
        { key: 'species_id', value: variables.speciesId },
        { key: 'season', value: variables.season },
        { key: 'date_from', value: variables.dateFrom },
        { key: 'date_to', value: variables.dateTo },
        { key: 'exporter', value: 'FRUSAN' },
        { key: 'follow_id', value: '' },
      ],
      item: [
        {
          name: '1. Status',
          request: {
            method: 'GET',
            header: [authHeader],
            url: '{{base_url}}/status',
          },
        },
        {
          name: '2. List exporters',
          request: {
            method: 'GET',
            header: [authHeader],
            url: '{{base_url}}/exporters?species_id={{species_id}}&season={{season}}',
          },
        },
        {
          name: '3. Follow exporter',
          request: {
            method: 'POST',
            header: [
              authHeader,
              { key: 'Content-Type', value: 'application/json', type: 'text' },
            ],
            body: {
              mode: 'raw',
              raw: JSON.stringify(
                {
                  species_id: Number(variables.speciesId),
                  season: variables.season,
                  exporter: '{{exporter}}',
                },
                null,
                2
              ),
              options: { raw: { language: 'json' } },
            },
            url: '{{base_url}}/followed-exporters',
          },
          event: [
            {
              listen: 'test',
              script: {
                type: 'text/javascript',
                exec: [
                  'const payload = pm.response.json();',
                  "if (payload.data?.id) pm.collectionVariables.set('follow_id', payload.data.id);",
                ],
              },
            },
          ],
        },
        {
          name: '4. List followed exporters',
          request: {
            method: 'GET',
            header: [authHeader],
            url: '{{base_url}}/followed-exporters?species_id={{species_id}}&season={{season}}',
          },
        },
        {
          name: '5. Delete followed exporter',
          request: {
            method: 'DELETE',
            header: [authHeader],
            url: '{{base_url}}/followed-exporters/{{follow_id}}',
          },
        },
        {
          name: '6. Daily Market - FOB per box',
          request: {
            method: 'GET',
            header: [authHeader],
            url: '{{base_url}}/market-data/daily-market?species_id={{species_id}}&season={{season}}&date_from={{date_from}}&date_to={{date_to}}&grain=week&price_level=fob&unit=box&box_weight_kg=5&exporter={{exporter}}',
          },
        },
      ],
    });
    toast.success('Colección Postman descargada con el token.');
  };

  const grantFields = (
    form: GrantForm,
    setForm: React.Dispatch<React.SetStateAction<GrantForm>>
  ) => (
    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
      <Select
        label='Especie'
        selectedKeys={form.speciesId ? [form.speciesId] : []}
        onChange={(event) =>
          setForm((current) => ({
            ...current,
            speciesId: event.target.value,
          }))
        }
        isRequired
      >
        {species.map((item) => (
          <SelectItem key={String(item.id)} value={String(item.id)}>
            {item.name}
          </SelectItem>
        ))}
      </Select>
      <Select
        label='Temporada'
        selectedKeys={form.season ? [form.season] : []}
        onChange={(event) =>
          setForm((current) => ({ ...current, season: event.target.value }))
        }
        description='Usa “Todas” solo si el contrato no limita temporadas.'
        isRequired
      >
        {[...seasonOptions, '*'].map((season) => (
          <SelectItem key={season} value={season}>
            {season === '*' ? 'Todas las temporadas (*)' : season}
          </SelectItem>
        ))}
      </Select>
      <Input
        type='date'
        label='Datos desde (opcional)'
        value={form.dataFrom}
        onValueChange={(value) =>
          setForm((current) => ({ ...current, dataFrom: value }))
        }
      />
      <Input
        type='date'
        label='Datos hasta (opcional)'
        value={form.dataTo}
        onValueChange={(value) =>
          setForm((current) => ({ ...current, dataTo: value }))
        }
      />
    </div>
  );

  const keyFields = (
    form: KeyForm,
    setForm: React.Dispatch<React.SetStateAction<KeyForm>>
  ) => (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
        <Input
          label='Nombre de la credencial'
          value={form.name}
          onValueChange={(value) =>
            setForm((current) => ({ ...current, name: value }))
          }
          isRequired
        />
        <Input
          type='date'
          label='Vence el (opcional)'
          value={form.expiresOn}
          onValueChange={(value) =>
            setForm((current) => ({ ...current, expiresOn: value }))
          }
          min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
        />
      </div>
      <div>
        <p className='mb-2 text-sm font-medium text-foreground'>Permisos</p>
        <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
          {ALL_SCOPES.map((scope) => (
            <Checkbox
              key={scope.value}
              isSelected={form.scopes.includes(scope.value)}
              onValueChange={(checked) =>
                toggleScope(scope.value, checked, setForm)
              }
              classNames={{ label: 'w-full' }}
            >
              <span className='block text-sm font-medium'>{scope.label}</span>
              <span className='block text-xs text-default-500'>
                {scope.description}
              </span>
            </Checkbox>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout className='flex flex-col gap-6'>
      <PageHeader
        title='Acceso API Market Data'
        description='Vincula un cliente con su exportadora, define su acceso de datos y genera la credencial que entregarás junto con la documentación.'
        actions={
          <Button
            variant='flat'
            startContent={<Icon icon='tabler:refresh' width={18} />}
            onPress={() => void loadData()}
            isLoading={loading}
          >
            Actualizar
          </Button>
        }
      />

      <Card shadow='sm'>
        <CardHeader className='flex-col items-start gap-1 px-6 pt-6'>
          <div className='flex items-center gap-2'>
            <span className='flex h-7 w-7 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground'>
              1
            </span>
            <h2 className='text-lg font-semibold'>Provisionar cliente</h2>
          </div>
          <p className='text-sm text-default-500'>
            Esta acción crea el cliente, otorga el acceso y emite el token en un
            solo flujo.
          </p>
        </CardHeader>
        <CardBody className='space-y-6 px-6 pb-6'>
          <section className='space-y-3'>
            <h3 className='text-sm font-semibold uppercase tracking-wide text-default-500'>
              Cliente y límites
            </h3>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4'>
              <Select
                className='lg:col-span-2'
                label='Exportadora cliente'
                placeholder='Selecciona una exportadora'
                selectedKeys={exporterId ? [exporterId] : []}
                onChange={(event) => handleExporterChange(event.target.value)}
                isRequired
              >
                {exporters.map((exporter) => (
                  <SelectItem
                    key={String(exporter.id)}
                    value={String(exporter.id)}
                    textValue={exporter.name}
                  >
                    {exporter.name}
                    {exporter.isActive === false ? ' (inactiva)' : ''}
                  </SelectItem>
                ))}
              </Select>
              <Input
                className='lg:col-span-2'
                label='Nombre del cliente API'
                value={clientName}
                onValueChange={(value) => {
                  setClientName(value);
                  setClientNameTouched(true);
                }}
                isRequired
              />
              <Input
                type='number'
                min='1'
                max='10000'
                label='Solicitudes por minuto'
                value={minuteLimit}
                onValueChange={setMinuteLimit}
                isRequired
              />
              <Input
                type='number'
                min='1'
                max='1000000'
                label='Solicitudes por día'
                value={dailyLimit}
                onValueChange={setDailyLimit}
                isRequired
              />
            </div>
          </section>

          <section className='space-y-3'>
            <h3 className='text-sm font-semibold uppercase tracking-wide text-default-500'>
              Acceso de datos
            </h3>
            {grantFields(grantForm, setGrantForm)}
          </section>

          <section className='space-y-3'>
            <h3 className='text-sm font-semibold uppercase tracking-wide text-default-500'>
              Credencial
            </h3>
            {keyFields(keyForm, setKeyForm)}
          </section>

          <div className='flex flex-col items-start justify-between gap-3 rounded-medium border border-default-200 p-4 sm:flex-row sm:items-center'>
            <div className='flex items-start gap-2 text-sm text-default-600'>
              <Icon
                icon='tabler:shield-lock'
                width={20}
                className='mt-0.5 shrink-0 text-success'
              />
              <span>
                El token se muestra una sola vez. Cherry Market guarda
                únicamente el hash de su secreto.
              </span>
            </div>
            <Button
              color='primary'
              size='lg'
              onPress={() => void handleProvision()}
              isLoading={provisioning}
              startContent={
                provisioning ? undefined : <Icon icon='tabler:key' width={20} />
              }
            >
              {provisioning ? provisioningStep : 'Crear acceso y generar token'}
            </Button>
          </div>
        </CardBody>
      </Card>

      <section className='space-y-3'>
        <div>
          <h2 className='text-xl font-semibold'>Clientes API</h2>
          <p className='text-sm text-default-500'>
            Administra accesos, rota credenciales y suspende clientes sin
            afectar a los usuarios del portal.
          </p>
        </div>

        {loading && clients.length === 0 ? (
          <LoadingState label='Cargando clientes API…' />
        ) : clients.length === 0 ? (
          <EmptyState title='Todavía no hay clientes API.' />
        ) : (
          <div className='space-y-4'>
            {clients.map((client) => {
              const exporter = exporterById.get(client.exporter_id ?? -1);
              const grants = client.external_api_client_grants ?? [];
              const keys = client.external_api_keys ?? [];
              return (
                <Card key={client.id} shadow='sm'>
                  <CardHeader className='flex flex-col items-start gap-4 px-5 pt-5 md:flex-row md:items-center md:justify-between'>
                    <div>
                      <div className='flex flex-wrap items-center gap-2'>
                        <h3 className='text-lg font-semibold'>{client.name}</h3>
                        <Chip
                          size='sm'
                          color={statusColor(client.status)}
                          variant='flat'
                        >
                          {client.status}
                        </Chip>
                      </div>
                      <p className='text-sm text-default-500'>
                        {exporter?.name ?? 'Sin exportadora vinculada'} ·{' '}
                        {client.minute_limit}/min · {client.daily_limit}/día
                      </p>
                    </div>
                    <div className='flex flex-wrap gap-2'>
                      <Button
                        size='sm'
                        variant='flat'
                        startContent={<Icon icon='tabler:plus' width={16} />}
                        onPress={() => openGrantModal(client)}
                      >
                        Agregar acceso
                      </Button>
                      <Button
                        size='sm'
                        variant='flat'
                        startContent={<Icon icon='tabler:key' width={16} />}
                        onPress={() => openKeyModal(client)}
                        isDisabled={client.status !== 'active'}
                      >
                        Nueva credencial
                      </Button>
                      {client.status !== 'revoked' && (
                        <Button
                          size='sm'
                          variant='flat'
                          onPress={() => void handleClientStatus(client)}
                          isLoading={updatingClientId === client.id}
                        >
                          {client.status === 'active'
                            ? 'Suspender'
                            : 'Reactivar'}
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardBody className='grid grid-cols-1 gap-5 px-5 pb-5 lg:grid-cols-2'>
                    <div className='rounded-xl border border-default-200 p-4'>
                      <h4 className='mb-3 flex items-center gap-2 text-sm font-semibold'>
                        <Icon icon='tabler:database' width={18} />
                        Accesos de datos
                      </h4>
                      {grants.length === 0 ? (
                        <p className='text-sm text-warning'>
                          Sin acceso: las consultas serán rechazadas.
                        </p>
                      ) : (
                        <div className='space-y-2'>
                          {grants.map((grant) => (
                            <div
                              key={grant.id}
                              className='flex items-center justify-between gap-3 rounded-lg bg-default-50 p-3'
                            >
                              <div>
                                <p className='text-sm font-medium'>
                                  {speciesById.get(grant.species_id)?.name ??
                                    `Especie ${grant.species_id}`}{' '}
                                  ·{' '}
                                  {grant.season === '*'
                                    ? 'Todas'
                                    : grant.season}
                                </p>
                                <p className='text-xs text-default-500'>
                                  {grant.data_from || 'Sin inicio'} →{' '}
                                  {grant.data_to || 'Sin término'}
                                </p>
                              </div>
                              <Button
                                isIconOnly
                                size='sm'
                                color='danger'
                                variant='light'
                                aria-label='Eliminar acceso'
                                onPress={() => void handleDeleteGrant(grant)}
                                isLoading={removingId === grant.id}
                              >
                                <Icon icon='tabler:trash' width={17} />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className='rounded-xl border border-default-200 p-4'>
                      <h4 className='mb-3 flex items-center gap-2 text-sm font-semibold'>
                        <Icon icon='tabler:key' width={18} />
                        Credenciales
                      </h4>
                      {keys.length === 0 ? (
                        <p className='text-sm text-warning'>
                          Sin credenciales emitidas.
                        </p>
                      ) : (
                        <div className='space-y-2'>
                          {keys.map((key) => (
                            <div
                              key={key.id}
                              className='flex items-center justify-between gap-3 rounded-lg bg-default-50 p-3'
                            >
                              <div className='min-w-0'>
                                <div className='flex flex-wrap items-center gap-2'>
                                  <p className='truncate text-sm font-medium'>
                                    {key.name}
                                  </p>
                                  <Chip
                                    size='sm'
                                    color={
                                      key.status === 'active'
                                        ? 'success'
                                        : 'danger'
                                    }
                                    variant='dot'
                                  >
                                    {key.status}
                                  </Chip>
                                </div>
                                <p className='truncate font-mono text-xs text-default-500'>
                                  ID: {key.key_id} · Último uso:{' '}
                                  {formatDateTime(key.last_used_at)}
                                </p>
                                <p className='mt-1 text-xs text-default-500'>
                                  {key.scopes.map(scopeLabel).join(' · ')}
                                </p>
                              </div>
                              {key.status === 'active' && (
                                <Button
                                  size='sm'
                                  color='danger'
                                  variant='light'
                                  onPress={() => void handleRevokeKey(key.id)}
                                  isLoading={removingId === key.id}
                                >
                                  Revocar
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <Modal
        isOpen={grantClient !== null}
        onOpenChange={(open) => !open && setGrantClient(null)}
        size='2xl'
      >
        <ModalContent>
          <ModalHeader>Agregar acceso · {grantClient?.name}</ModalHeader>
          <ModalBody>
            {grantFields(grantModalForm, setGrantModalForm)}
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={() => setGrantClient(null)}>
              Cancelar
            </Button>
            <Button
              color='primary'
              onPress={() => void handleAddGrant()}
              isLoading={savingGrant}
            >
              Guardar acceso
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={keyClient !== null}
        onOpenChange={(open) => !open && setKeyClient(null)}
        size='2xl'
      >
        <ModalContent>
          <ModalHeader>Nueva credencial · {keyClient?.name}</ModalHeader>
          <ModalBody>{keyFields(keyModalForm, setKeyModalForm)}</ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={() => setKeyClient(null)}>
              Cancelar
            </Button>
            <Button
              color='primary'
              onPress={() => void handleIssueKey()}
              isLoading={issuingKey}
            >
              Generar token
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={issuedCredential !== null}
        isDismissable={false}
        isKeyboardDismissDisabled
        hideCloseButton
        size='4xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader className='flex-col items-start gap-1'>
            <div className='flex items-center gap-2 text-success'>
              <Icon icon='tabler:circle-check-filled' width={24} />
              Token generado
            </div>
            <p className='text-sm font-normal text-default-500'>
              Esta es la única vez que Cherry Market mostrará el token completo.
            </p>
          </ModalHeader>
          <ModalBody className='space-y-5'>
            <Alert variant='warning'>
              Compártelo por un canal seguro. No lo envíes en el mismo correo
              que la documentación ni lo pegues en código de frontend.
            </Alert>

            <div>
              <p className='mb-2 text-sm font-medium'>Token</p>
              <div className='flex flex-col gap-2 sm:flex-row'>
                <Input
                  readOnly
                  value={issuedCredential?.key.token ?? ''}
                  classNames={{ input: 'font-mono text-xs' }}
                />
                <Button
                  color='primary'
                  startContent={<Icon icon='tabler:copy' width={18} />}
                  onPress={() =>
                    void copyText(
                      issuedCredential?.key.token ?? '',
                      'Token copiado.'
                    )
                  }
                >
                  Copiar token
                </Button>
              </div>
            </div>

            <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
              <Button
                variant='flat'
                startContent={<Icon icon='tabler:clipboard-copy' width={18} />}
                onPress={() =>
                  void copyText(handoffText, 'Instrucciones copiadas.')
                }
              >
                Copiar entrega completa
              </Button>
              <Button
                variant='flat'
                startContent={<Icon icon='tabler:brand-postman' width={18} />}
                onPress={downloadPostmanCollection}
              >
                Descargar Postman
              </Button>
              <Button
                as='a'
                href={customerGuideUrl}
                download='cherry-market-api-customer-guide.md'
                variant='flat'
                startContent={<Icon icon='tabler:file-text' width={18} />}
              >
                Guía del cliente
              </Button>
              <Button
                as='a'
                href={openApiUrl}
                download='cherry-market-api.openapi.yaml'
                variant='light'
                startContent={<Icon icon='tabler:file-code' width={18} />}
              >
                OpenAPI YAML
              </Button>
            </div>

            <div className='rounded-xl bg-default-100 p-4'>
              <p className='mb-2 text-sm font-medium'>
                Primera prueba en Postman
              </p>
              <pre className='overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs text-default-700'>
                {`GET ${externalMarketApiBaseUrl}/status\nAuthorization: Bearer ${issuedCredential?.key.token ?? ''}`}
              </pre>
            </div>

            <Checkbox
              isSelected={credentialSaved}
              onValueChange={setCredentialSaved}
            >
              Confirmo que guardé el token o descargué la colección de Postman.
            </Checkbox>
          </ModalBody>
          <ModalFooter>
            <Button
              color='primary'
              isDisabled={!credentialSaved}
              onPress={() => {
                setIssuedCredential(null);
                setCredentialSaved(false);
              }}
            >
              Terminar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminApiAccess;
